import { Box, Grid, TextField } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { ConfirmationDialogResult, RecordUserAttachmentCategory } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { IIdTitle, IdTitle } from "../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/models/common/idTitle";
import { AppUtils } from "../../../../framework/appUtils";
import * as store from "../../../../framework/customStore";
import { SaveDataObj } from "../../../../framework/saveData";
import { IRecordEditItemEmployment, RecordEditItemEmployment, RecordEditItemEmploymentSave } from "../../../../models/record/recordEditItemEmployment";
import * as recordService from "../../../../models/services/recordService";
import * as storeActions from "../../../../models/store/storeActions";
import { StoreUtils } from "../../../../models/store/storeUtils";
import { DatePicker } from "../../../framework/DateTime/DatePicker";
import { Dialog } from "../../../framework/Dialog/Dialog";
import { DialogButtons } from "../../../framework/Dialog/DialogButtons";
import { DialogItemsNav } from "../../../framework/Dialog/DialogItemsNav";
import { Autocomplete } from "../../../framework/Form/Autocomplete";
import { FormField } from "../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../../framework/Grid/GridRow";
import { RecordEditAttachmentList } from "../RecordEditAttachmentList";

export interface IRecordUserEmploymentDialogProp {
    classes?: string;
    companyId: string;
    recordId: string;
    userCode: string;
    item: IRecordEditItemEmployment;
    attachments: IFileAttachment[];
    jobTitles: string[];
    hasPrevious: boolean;
    hasNext: boolean;
    onSaved: (item: IRecordEditItemEmployment, closeDialog: boolean) => void;
    onClose: () => void;
    onPrevious: () => void;
    onNext: () => void;
    onAttachmentsRemoved: (ids: string[]) => void;
    onAttachmentsAdded: (attachments: IFileAttachment[]) => void;
}

export interface IRecordUserEmploymentDialogState {
    startDate: string;
    endDate: string;
    companyName: string;
    jobTitle: string;
    comment: string;
    attachments: IFileAttachment[];
    jobTitles: IIdTitle[];
}

export class RecordUserEmploymentDialog extends React.Component<IRecordUserEmploymentDialogProp, IRecordUserEmploymentDialogState> {
    private orgStateHash: string = "";
    
    getStateFromProps = (props: IRecordUserEmploymentDialogProp): IRecordUserEmploymentDialogState => {
        const item = props.item;
        return {
            startDate: Base.utcTimeToDateStr(item.startDate),
            endDate: Base.utcTimeToDateStr(item.endDate),
            companyName: item.companyName,
            jobTitle: item.jobTitle,
            comment: item.comment,
            attachments: props.attachments.filter(i => i.subOwnerId === item.id),
            jobTitles: props.jobTitles.map(i => IdTitle.createIdTitleSimple(i)),
        };
    };

    constructor(props: IRecordUserEmploymentDialogProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getHashFromState(props, this.state);
    }

    componentDidUpdate(prevProps: IRecordUserEmploymentDialogProp, prevState: IRecordUserEmploymentDialogState) {
        const props = this.props;
        if (prevProps.item.getHash() === props.item.getHash() && EditItem.getHash(prevProps.attachments) === EditItem.getHash(props.attachments)) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getHashFromState(props, state);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "companyName") {
            this.setState({ companyName: value });
        } else if (name === "jobTitle") {
            this.setState({ jobTitle: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    dateChange = (startDate: Date, endDate: Date) => {
        if (!Base.isNullOrUndefined(startDate) && !Base.isNullOrUndefined(endDate) && endDate < startDate) {
            endDate = startDate;
        }
        const startDateStr = Base.utcTimeToDateStr(startDate.getTime());
        const endDateStr = endDate ? Base.utcTimeToDateStr(endDate.getTime()) : "";
        this.setState({
            startDate: startDateStr,
            endDate: endDateStr
        });
    };

    handleStartDateChange = (value: string) => {
        this.setState({ startDate: value });
    };

    handleStartDateBlur = (value: string) => {
        const state = this.state;
        this.dateChange(value ? value.toUtcDate() : Base.getNowUtcDate().addDays(-1), state.endDate ? state.endDate.toUtcDate() : null);
    };

    handleEndDateChange = (value: string) => {
        this.setState({ endDate: value });
    };

    handleEndDateBlur = (value: string) => {
        const state = this.state;
        this.dateChange(state.startDate ? state.startDate.toUtcDate() : Base.getNowUtcDate().addDays(-1), value ? value.toUtcDate() : null);
    };

    getHashFromState = (props: IRecordUserEmploymentDialogProp, state: IRecordUserEmploymentDialogState): string => {
        const result = new SaveDataObj();
        result.append("startDate", state.startDate);
        result.append("endDate", !state.endDate ? null : state.endDate);
        result.append("companyName", state.companyName);
        result.append("jobTitle", state.jobTitle);
        result.append("comment", state.comment);
        return result.hash;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.jobTitle) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.JobTitleMustBeDefined));
                return resolve(false);
            }
            return resolve(true);
        });
    };
    
    getSaveData = (props: IRecordUserEmploymentDialogProp, employment: RecordEditItemEmploymentSave): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("employment", employment);
        return result;
    };

    saveToDb = async (): Promise<RecordEditItemEmployment> => {
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return null;
        // Set data
        const item = new RecordEditItemEmployment(this.props.item);
        item.startDate = state.startDate.toUtcDate().getTime();
        item.endDate = state.endDate ? state.endDate.toUtcDate().getTime() : null;
        item.companyName = state.companyName;
        item.jobTitle = state.jobTitle;
        item.comment = state.comment;
        const saveData = this.getSaveData(this.props, new RecordEditItemEmploymentSave(item));
        // Call server
        const result = await AppUtils.callService(() => recordService.recordEditSaveEmployment(saveData.obj));
        if (!result) return null;
        //Update state and hash
        this.orgStateHash = this.getHashFromState(this.props, this.state);
        item.id = result.id;
        item.rowId = result.rowId;
        item.modifyUser = StoreUtils.getNowModifyUser();
        return item;
    };

    saveDialog = async (close: boolean) => {
        const result = await this.saveToDb();
        if (result) {
            this.props.onSaved(result, close);
        }
        return result;
    };

    handleSave = async (closeDialog: boolean): Promise<RecordEditItemEmployment> => {
        return await this.saveDialog(closeDialog);
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose();
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getHashFromState(props, this.state), this.orgStateHash,
            async () => !!await this.saveDialog(false),
            null,
            (newStateHash) => { this.orgStateHash = newStateHash; });
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        const isNew = props.item.isNew();
        return (
            <Dialog
                id="record-user-employment-dialog"
                maxWidth="sm"
                title={isNew ? Translations.AddEmployment : Translations.EditEmployment + ": " + props.item.getUiTitle()}
                body={
                    <>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.JobTitle} required margin={0}>
                                    <Autocomplete
                                        value={state.jobTitle}
                                        maxLength={100}
                                        options={state.jobTitles}
                                        onChange={(value: string) => this.setState({ jobTitle: value })}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.Company}>
                                    <TextField type="text" name="companyName" title={Translations.Company} inputProps={{ maxLength: 100 }} value={state.companyName} required={true} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.EmploymentStartDate} required>
                                    <DatePicker
                                        value={state.startDate}
                                        required={true}
                                        onChange={this.handleStartDateChange}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item xs>
                                <FormField title={Translations.EmploymentEndDate}>
                                    <DatePicker
                                        value={state.endDate}
                                        onChange={this.handleEndDateChange}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.AdditionalInformation}>
                                    <TextField name="comment" title={Translations.AdditionalInformation} inputProps={{ maxLength: 1000 }} multiline minRows={5} disabled={false} value={state.comment} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        {!!props.onAttachmentsAdded &&
                            <GridRow>
                                <Grid item xs>
                                    <RecordEditAttachmentList
                                        isDisabled={isNew}
                                        isDisabledMessage={Translations.NewEmploymentMustBeSavedBeforeAttachmentsCanBeAdded}
                                        companyId={props.companyId}
                                        recordId={props.recordId}
                                        subOwnerId={props.item.id}
                                        userCode={props.userCode}
                                        category={RecordUserAttachmentCategory.Employment}
                                        attachments={state.attachments}
                                        onRemoved={props.onAttachmentsRemoved}
                                        onAdded={props.onAttachmentsAdded}
                                    />
                                </Grid>
                            </GridRow>
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!isNew ? props.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={!isNew ? this.handlePrevious : null}
                            onNext={!isNew ? this.handleNext : null}
                        />
                        <DialogButtons
                            isNew={isNew}
                            onCancel={this.handleCancel}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}